import type { DeploymentStatus, OverridableServiceName } from './endpoints';

/**
 * Pure, framework-free helpers for the PTV2-1021 Phase 2 override UI: deployment
 * status classification + pill mapping, resource-usage math, and create/edit
 * override-draft validation. Kept separate from `api.ts` so they're trivially
 * unit-testable and reusable across the table, detail panel, and create dialog.
 */

const NON_TERMINAL_STATUSES: ReadonlySet<DeploymentStatus> = new Set<DeploymentStatus>([
  'pending',
  'pulling',
  'deploying',
  'wiping',
]);

const FAILED_STATUSES: ReadonlySet<DeploymentStatus> = new Set<DeploymentStatus>([
  'failed_deploy',
  'failed_update',
  'failed_stop',
  'failed_wipe',
]);

/**
 * A non-terminal deployment status is one the UI must keep polling for
 * (`pending | pulling | deploying | wiping`). `null` (plain tenant) and the
 * terminal states (`ready | stopped | failed_*`) do not poll.
 */
export function isNonTerminalDeploymentStatus(status: DeploymentStatus | null | undefined): boolean {
  return status != null && NON_TERMINAL_STATUSES.has(status);
}

export function isFailedDeploymentStatus(status: DeploymentStatus | null | undefined): boolean {
  return status != null && FAILED_STATUSES.has(status);
}

export type DeploymentTone = 'success' | 'progress' | 'neutral' | 'destructive';

export interface DeploymentStatusDescriptor {
  readonly tone: DeploymentTone;
  /** Whether the status icon should spin (in-flight). */
  readonly spin: boolean;
  /** Stable i18n key for the human label. */
  readonly i18nKey: string;
}

/**
 * Maps a deployment status to a tone + spin + i18n key for the status pill.
 * `wiping` is destructive AND animated (a schema is being dropped). Failures
 * are destructive. In-flight non-wipe states are an animated "progress" tone.
 */
export function describeDeploymentStatus(status: DeploymentStatus): DeploymentStatusDescriptor {
  const i18nKey = `routes.platform.tenants.deploymentStatus.${status}`;
  if (status === 'ready') return { tone: 'success', spin: false, i18nKey };
  if (status === 'stopped') return { tone: 'neutral', spin: false, i18nKey };
  if (status === 'wiping') return { tone: 'destructive', spin: true, i18nKey };
  if (isFailedDeploymentStatus(status)) return { tone: 'destructive', spin: false, i18nKey };
  // pending | pulling | deploying
  return { tone: 'progress', spin: true, i18nKey };
}

/** True once any tenant carries a non-terminal deployment status (drives polling). */
export function hasNonTerminalDeployment(
  tenants: readonly { readonly deploymentStatus: DeploymentStatus | null }[],
): boolean {
  return tenants.some((t) => isNonTerminalDeploymentStatus(t.deploymentStatus));
}

const BYTES_PER_GB = 1024 ** 3;

/** Human-readable GB with one decimal (e.g. `2.0 GB`). */
export function formatGb(bytes: number): string {
  const gb = bytes / BYTES_PER_GB;
  return `${gb.toFixed(1)} GB`;
}

/** Clamped 0–100 usage percentage; 0 when the ceiling is non-positive. */
export function resourceUsagePercent(usedBytes: number, ceilingBytes: number): number {
  if (ceilingBytes <= 0) return 0;
  const pct = (usedBytes / ceilingBytes) * 100;
  if (pct < 0) return 0;
  if (pct > 100) return 100;
  return pct;
}

// ── Create / edit override-draft validation ────────────────────────────────

export interface OverrideDraftEntry {
  readonly checked: boolean;
  readonly tag: string;
}

export type OverrideDraft = Readonly<Record<OverridableServiceName, OverrideDraftEntry>>;

export type OverrideDraftErrors = Partial<Record<OverridableServiceName, 'tagRequired'>>;

/**
 * Validates an override draft: a checked service must carry a non-empty
 * (trimmed) image tag. Unchecked services are ignored entirely.
 */
export function validateOverrideDraft(draft: OverrideDraft): OverrideDraftErrors {
  const errors: OverrideDraftErrors = {};
  (Object.keys(draft) as OverridableServiceName[]).forEach((service) => {
    const entry = draft[service];
    if (entry.checked && entry.tag.trim().length === 0) {
      errors[service] = 'tagRequired';
    }
  });
  return errors;
}
